import React, { useMemo } from "react";
import moment from "moment";

const PrayerTimes = ({ prayerData, textColor, timingColor, highlightColor, currentTime }) => {
  
  const { prayers, nextPrayerIndex } = useMemo(() => {
    const iqamah = prayerData?.iqamatimes || {};
    const adhan = prayerData?.startTimes?.timings || {};

    if (!iqamah || !adhan) {
      return { prayers: [], nextPrayerIndex: -1 };
    }

    // 1. Build Basic Prayers
    const list = [
      { name: "FAJR", azan: adhan.fajr || adhan.fajar, iqamah: iqamah.fajr || iqamah.fajar },
      { name: "DHUHR", azan: adhan.dhuhr || adhan.zuhr, iqamah: iqamah.dhuhr || iqamah.zuhr },
      { name: "ASR", azan: adhan.asr, iqamah: iqamah.asr },
      { name: "MAGHRIB", azan: adhan.maghrib, iqamah: iqamah.maghrib },
      { name: "ISHA", azan: adhan.isha, iqamah: iqamah.isha },
    ];

    // 2. Append Dynamic Jummahs (1-3)
    const jt = prayerData?.jummahTimes;
    if (Array.isArray(jt) && jt.length > 0) {
      const sortedJummah = [...jt].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
      sortedJummah.forEach((j, idx) => {
        const name = sortedJummah.length === 1 ? "JUMMAH" : `JUMMAH ${idx + 1}`;
        list.push({
          name: name,
          azan: j.khutba_Time || "—",
          iqamah: j.iqama_Time || "—",
          isJummah: true 
        });
      });
    }

    // 3. Calculate "Next" Prayer (Robust Logic from previous fix)
    let activeIndex = 0; // Default to Fajr
    let fajrSkipped = false;

    if (currentTime) {
      const now = currentTime; 
      let foundIndex = -1;
      
      for (let i = 0; i < 5; i++) { 
        const p = list[i];
        const timeStr = (p.iqamah && p.iqamah !== "—") ? p.iqamah : p.azan;

        if (!timeStr || timeStr === "—") {
            if (i === 0) fajrSkipped = true;
            continue;
        }

        const targetTime = moment(timeStr, ["h:mm A", "h:mm a", "HH:mm", "HH:mm:ss"]);
        
        if (!targetTime.isValid()) {
            if (i === 0) fajrSkipped = true;
            continue;
        }

        targetTime.set({
            year: now.year(),
            month: now.month(),
            date: now.date()
        });

        if (now.isBefore(targetTime)) {
          foundIndex = i;
          break; 
        }
      }

      // Logic fix: If Fajr was missing & it's morning -> Force Highlight Fajr
      if (foundIndex > 0 && fajrSkipped && now.hour() < 12) {
          activeIndex = 0; 
      } else if (foundIndex !== -1) {
          activeIndex = foundIndex;
      }
    }

    return { prayers: list, nextPrayerIndex: activeIndex };
  }, [prayerData, currentTime]);

  if (prayers.length === 0) {
    return <div className="text-center mt-10" style={{ color: textColor }}>Loading...</div>;
  }

  // Layout: Horizontal Flex Row (Squeezed)
  return (
    <div className="w-full px-[2svh] mt-[4svh]">
      <div className="flex flex-nowrap w-full gap-[1svw] text-center justify-between">
        
        {prayers.map((prayer, index) => {
          const isNext = index === nextPrayerIndex;

          return (
            <div
              key={`${prayer.name}-${index}`}
              className={`flex-1 min-w-0 flex flex-col items-center py-[2svh] rounded-lg transition-all duration-500 ${
                !isNext ? "border-r-2 border-gray-700" : ""
              }`}
              style={{ 
                // Highlight Styling
                backgroundColor: isNext ? `${highlightColor}33` : "transparent",
                border: isNext ? `2px solid ${highlightColor}` : index !== prayers.length - 1 ? "" : "2px solid transparent",
                transform: isNext ? "scale(1.05)" : "scale(1)",
                boxShadow: isNext ? `0 0 15px ${highlightColor}66` : "none",
                borderRight: isNext ? `2px solid ${highlightColor}` : undefined 
              }}
            >
              {/* Prayer Name */}
              <div 
                className="font-bold mb-[0.5svh] truncate w-full" 
                style={{ 
                    color: isNext ? highlightColor : textColor,
                    fontSize: "clamp(4svh, 3.5svh, 2svh)" 
                }}
              >
                {prayer.name}
              </div>

              {/* Azan Time */}
              <div 
                className="font-semibold truncate w-full" 
                style={{ 
                    color: highlightColor,
                    fontSize: "clamp(4svh, 4svh, 2.5svh)"
                }}
              >
                {prayer.azan}
              </div>

              {/* Iqamah Time */}
              <div 
                className="font-semibold truncate w-full" 
                style={{ 
                    color: timingColor,
                    fontSize: "clamp(4svh, 4svh, 2.5svh)"
                }}
              >
                {prayer.iqamah}
              </div>
            </div>
          );
        })}
      </div>

      <div className="w-full mt-[3svh]">
        <hr className="border-t-[0.6svh]" style={{ borderColor: textColor }} />
      </div>
    </div>
  );
};

export default PrayerTimes;